import { useContext, useRef } from 'react';
import { Table as TableAntd } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import TableCard from '../../../../../components/TableCard';
import TableActionButton from '../../../../../components/TableActionButton';
import { useGetColumnSearchPropsData } from '../../../../../hooks/FilterTable/date';
import { useGetColumnSearchProps } from '../../../../../hooks/FilterTable/stringClearFiltros';
import { ICityTax } from '../../../../../interfaces';
import { formatDateStringToDate, formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { formatCpf, formatCurrency } from '../../../../../utils/Masks';
import StatusTag from '../../../../CityTaxes/components/StatusTag';
import { ThemeContext } from '../../../../../contexts/ThemeContext';

interface ITableData extends ICityTax {
    key: number;
    app_user_cpf?: string;
}

interface IProps {
    taxes: ICityTax[] | undefined;
}

export default function TabTaxes({
    taxes,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const payment_modalities: Record<string, string> = {
        COTA_UNICA: "Cota Única",
        PARCELA: "Parcela",
    };

    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const columns = [
        {
            title: 'CPF',
            dataIndex: 'app_user_cpf',
            key: 'app_user_cpf',
            ...useGetColumnSearchProps({ dataIndex: 'app_user_cpf', ref: refFuncaoLimparFiltrosString, formatter: formatCpf }),
            render: (cpf: string) => <span style={{ whiteSpace: 'nowrap' }}>{formatCpf(cpf)}</span>,
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            render: (record: number) => <span style={{ fontWeight: 600, color: isDark ? '#fa8c16' : '#d46b08', whiteSpace: 'nowrap' }}>{record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00'}</span>,
            sorter: (a: ITableData, b: ITableData) => (a?.value || 0) - (b?.value || 0),
            showSorterTooltip: false,
            align: 'right' as const,
        },
        {
            title: 'Data de Vencimento',
            dataIndex: 'due_date',
            key: 'due_date',
            sorter: (a: ITableData, b: ITableData) => (a.due_date ? new Date(a.due_date).getTime() : 0) - (b.due_date ? new Date(b.due_date).getTime() : 0),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'due_date' }),
            render: (data_vencimento: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {data_vencimento ? formatDateStringToDate(data_vencimento) : '--'}
                </span>
            ),
        },
        {
            title: 'Imposto',
            dataIndex: 'type',
            key: 'type',
            sorter: (a: ITableData, b: ITableData) => a.type.localeCompare(b.type),
            filters: [
                { text: 'IPTU', value: 'IPTU' },
                { text: 'ITBI', value: 'ITBI' },
            ],
            onFilter: (value: any, record: ITableData) => record.type === value,
            render: (_: any, tax: ITableData) => tax.type,
        },
        {
            title: 'Modalidade',
            dataIndex: 'payment_modality',
            key: 'payment_modality',
            sorter: (a: ITableData, b: ITableData) => a.payment_modality.localeCompare(b.payment_modality),
            filters: [
                { text: 'Cota Única', value: 'COTA_UNICA' },
                { text: 'Parcela', value: 'PARCELA' },
            ],
            onFilter: (value: any, record: ITableData) => record.payment_modality === value,
            render: (_: any, tax: ITableData) => payment_modalities[tax.payment_modality] || tax.payment_modality,
        },
        {
            title: 'Parcela',
            dataIndex: 'installment',
            key: 'installment',
            sorter: (a: ITableData, b: ITableData) => (a?.installment || 0) - (b?.installment || 0),
            showSorterTooltip: false,
            align: 'center' as const,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            filters: [
                { text: 'Aprovado', value: 'Aprovado' },
                { text: 'Alerta', value: 'Alerta' },
                { text: 'Recusado', value: 'Erro' },
                { text: 'Em análise', value: 'Em análise' },
            ],
            onFilter: (value: any, record: ITableData) => record.status === value,
            render: (_: any, request: ITableData) => (
                <StatusTag
                    key={request.id}
                    status={request.status}
                    message={request.message}
                />
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (criado: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {criado ? formatDateStringToDateAndTime(criado) : '--'}
                </span>
            ),
            showSorterTooltip: false,
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '6%',
            render: (_: any, record: any) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar imposto"
                    onClick={() => navigate(`/impostos/municipais/${record.id}`)}
                />
            ),
        },
    ];

    if (!taxes) {
        return null;
    }

    return (
        <div style={{ paddingTop: 8 }}>
            <TableCard>
                <TableAntd
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={taxes.map((tax) => ({
                        ...tax,
                        key: tax.id,
                        app_user_cpf: tax.app_user?.cpf,
                    }))}
                    onRow={(record) => ({
                        onClick: () => {
                            navigate(`/impostos/municipais/${record.id}`);
                        },
                        style: {
                            cursor: 'pointer',
                        },
                    })}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                    }}
                />
            </TableCard>
        </div>
    );
}
